<script setup lang="ts">
/**
 * Archive PAGINÉE et PARCOURABLE du blog — /blog/archives/1, /2, /3…
 *
 * POURQUOI (audit SEO 2026-08-06) : le blog est le premier actif SEO de
 * Donoor (3 912 impressions sur 90 jours, 30 % du site) mais ses 154 articles
 * n'étaient atteignables par aucun chemin complet :
 *   - /blog expose 28 liens (page d'accueil éditoriale : à la une, derniers,
 *     catégories) ;
 *   - les 8 pages de catégorie chargent 12 articles chacune, et leur
 *     pagination est purement côté client (un `ref` incrémenté, aucun <a href>)
 *     → invisible pour un robot.
 * Soit ~58 articles sans aucun lien entrant interne, dépendant du seul sitemap.
 *
 * Cette archive donne à Google un chemin exhaustif vers chaque article, en
 * liens HTML réels rendus côté serveur. `/blog` reste la vitrine éditoriale :
 * on ne touche pas à sa mise en avant.
 *
 * Le segment est `archives` et non `page` pour ne pas entrer en collision avec
 * un éventuel article dont le slug serait « page » (/blog/[slug] est au même
 * niveau).
 */
import { useBlog } from "~/composables/useBlog";

const route = useRoute();
const pageParam = Number((route.params as Record<string, string>).n);

if (!Number.isInteger(pageParam) || pageParam < 1) {
  throw createError({ statusCode: 404, statusMessage: "Page introuvable" });
}

definePageMeta({ key: (route) => route.path });

const { fetchArticles, getImageUrl, onImageError, formatDate } = useBlog();
const PER_PAGE = 24;

const { data } = await fetchArticles({ page: pageParam, limit: PER_PAGE });

const articles = computed(() => data.value?.articles ?? []);
const totalPages = computed(() => Math.max(1, data.value?.totalPages ?? 1));
const total = computed(() => data.value?.total ?? 0);

// Page hors bornes : un vrai 404, jamais une page vide indexable.
if (pageParam > 1 && articles.value.length === 0) {
  throw createError({ statusCode: 404, statusMessage: "Page introuvable" });
}

const pagePath = (p: number) => `/blog/archives/${p}`;
const canonicalUrl = computed(() => `https://donoor.org${pagePath(pageParam)}`);
const prevHref = computed(() =>
  pageParam > 1 ? `https://donoor.org${pagePath(pageParam - 1)}` : null,
);
const nextHref = computed(() =>
  pageParam < totalPages.value ? `https://donoor.org${pagePath(pageParam + 1)}` : null,
);

const pageWindow = computed(() => {
  const nums = new Set<number>([1, totalPages.value, pageParam - 1, pageParam, pageParam + 1]);
  return [...nums].filter((n) => n >= 1 && n <= totalPages.value).sort((a, b) => a - b);
});

const seoTitle = computed(() =>
  pageParam > 1
    ? `Tous les articles du blog — Page ${pageParam}`
    : "Tous les articles du blog Donoor",
);
const seoDescription = computed(
  () =>
    `Les ${total.value} articles du blog Donoor : gestion d'association, collecte de dons, fiscalité, reçus fiscaux, adhésions et billetterie.`,
);

const robotsContent = useRobotsContent();

useSeoMeta({
  title: () => seoTitle.value,
  description: () => seoDescription.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => seoDescription.value,
  ogUrl: () => canonicalUrl.value,
  ogLocale: "fr_FR",
  robots: () => robotsContent.value,
});

useHead({
  // Canonical AUTO-RÉFÉRENT sur chaque page : pointer les pages 2+ vers la
  // page 1 ferait comprendre à Google que ce sont des doublons, et il
  // cesserait de les explorer — exactement ce qu'on cherche à éviter.
  link: computed(() => [
    { rel: "canonical" as const, key: "canonical", href: canonicalUrl.value },
    ...(prevHref.value ? [{ rel: "prev" as const, key: "prev", href: prevHref.value }] : []),
    ...(nextHref.value ? [{ rel: "next" as const, key: "next", href: nextHref.value }] : []),
  ]),
});
</script>

<template>
  <div class="min-h-screen bg-white">
    <div class="pt-32 sm:pt-36 pb-10 px-4 bg-[#fafcff] border-b border-gray-200">
      <UContainer>
        <nav class="text-sm text-gray-500 mb-3">
          <NuxtLink to="/blog" class="hover:underline">Blog</NuxtLink>
          <span class="mx-1.5">›</span>
          <span>Tous les articles</span>
        </nav>
        <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
          Tous les articles du blog
        </h1>
        <p class="mt-3 text-sm sm:text-base text-gray-600 max-w-2xl">
          {{ total }} articles pour créer, gérer et financer votre association.
        </p>
      </UContainer>
    </div>

    <div class="px-4 py-10 sm:py-12">
      <UContainer>
        <ul class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <li
            v-for="article in articles"
            :key="article.id"
            class="group rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
          >
            <NuxtLink :to="`/blog/${article.slug}`" class="block">
              <NuxtImg
                :src="getImageUrl(article.image, 600)"
                :alt="article.title"
                :data-asset-id="article.image"
                class="h-40 w-full object-cover"
                loading="lazy"
                width="600"
                height="320"
                @error="onImageError"
              />
              <div class="p-4">
                <p class="text-xs text-gray-400 mb-1">
                  {{ formatDate(article.date_published) }}
                </p>
                <h2 class="text-base font-semibold text-gray-900 leading-snug group-hover:text-(--color-main)">
                  {{ article.title }}
                </h2>
              </div>
            </NuxtLink>
          </li>
        </ul>

        <nav
          v-if="totalPages > 1"
          class="mt-10 flex items-center justify-center gap-2 flex-wrap"
          aria-label="Pagination des articles"
        >
          <NuxtLink
            v-if="pageParam > 1"
            :to="pagePath(pageParam - 1)"
            rel="prev"
            class="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50"
          >
            Précédent
          </NuxtLink>

          <template v-for="(n, i) in pageWindow" :key="n">
            <span
              v-if="i > 0 && n - (pageWindow[i - 1] ?? 0) > 1"
              class="px-1 text-gray-400 select-none"
              aria-hidden="true"
            >…</span>
            <NuxtLink
              :to="pagePath(n)"
              :aria-current="n === pageParam ? 'page' : undefined"
              class="min-w-10 px-3 py-2 rounded-lg text-sm font-semibold text-center border transition-colors"
              :class="
                n === pageParam
                  ? 'bg-main text-white border-transparent'
                  : 'text-gray-700 border-gray-200 hover:bg-gray-50'
              "
            >
              {{ n }}
            </NuxtLink>
          </template>

          <NuxtLink
            v-if="pageParam < totalPages"
            :to="pagePath(pageParam + 1)"
            rel="next"
            class="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50"
          >
            Suivant
          </NuxtLink>
        </nav>
      </UContainer>
    </div>
  </div>
</template>
